import React, { useState, useEffect, useRef } from "react"
import { useTranslations } from "modules/i18n/hooks/useTranslations"

// Importing @nprapps/sidechain above creates a custom element, which requires
// us to define a type for it so typescript doesn't complain.
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "side-chain": {
        src: string
        ref?: React.RefObject<HTMLElement | null>
      }
    }
  }
}

export interface IframeProps {
  src: string
  height?: number
  title?: string
}

type IsLoadedCallback = (b: boolean) => void

const lazyloadSidechain = async (isLoadedCallback: IsLoadedCallback) => {
  await import("@nprapps/sidechain")
  isLoadedCallback(true)
}

export const Iframe = ({ src, height, title }: IframeProps) => {
  const [loaded, setLoaded] = useState(false)
  const sidechainRef = useRef<HTMLElement>(null)
  const t = useTranslations("blocks.iframes")

  useEffect(() => {
    lazyloadSidechain(setLoaded)
  }, [loaded])

  // Handles updating the iframes title
  useEffect(() => {
    if (!loaded || !sidechainRef.current) return

    const sidechainEl = sidechainRef.current

    const updateIframeTitle = () => {
      const iframe = sidechainEl.shadowRoot?.querySelector("iframe")
      if (iframe && iframe.getAttribute("title") !== title) {
        iframe.setAttribute("title", title || t("default_accessible_title"))
      }
    }

    // Try straight away
    updateIframeTitle()

    // Setup an observer to watch for the iframe being loaded later, so we can set its title then
    const targetNode = sidechainEl.shadowRoot || sidechainEl

    const observer = new MutationObserver(() => {
      updateIframeTitle()
    })

    observer.observe(targetNode, {
      childList: true, // Watch for added/removed nodes
      subtree: true, // Watch deeply within the tree
    })

    return () => {
      observer.disconnect()
    }
  }, [loaded, title, t])

  // Render an empty placeholder with an appropriate height, if saved
  if (loaded) {
    return <side-chain ref={sidechainRef} src={src}></side-chain>
  } else {
    return <div style={{ minHeight: height || 0 }}></div>
  }
}
